<template>
  <MainLayout>
    <PageHeader>
      <template #pageTitle>Hello, {{ userStore.getFirstName() }}</template>
      <template #pageDescription></template>
    </PageHeader>

    <div class="overview-overline">
      <div class="overline--headline">Overview</div>
      <div class="overline--controls">
        <RouterLink to="/event-compare" class="p-button p-component p-button-outlined">
          <IconCompare /> Compare Events
        </RouterLink>
        <FeatureFlag name="premium_benchmarking">
          <RouterLink to="/benchmarking" class="p-button p-component p-button-outlined">
            <IconBenchmark /> Benchmark Events
            <Tooltip position="top">
              <template v-slot:text>
                <IconPremium class="premium-icon" />
              </template>
              <template v-slot:tooltip> Premium Feature </template>
            </Tooltip>
          </RouterLink>
        </FeatureFlag>
        <Tooltip position="left" :hasTooltip="!analyticsStore.chosenEventDetails?.event_completed">
          <template v-slot:text>
            <Button
              :disabled="
                !analyticsStore.chosenEvent || !analyticsStore.chosenEventDetails?.event_completed
              "
              outlined
              @click="downloadReport"
            >
              <IconDownload />
              Download Report
            </Button>
          </template>
          <template v-slot:tooltip> This event has not completed yet. </template>
        </Tooltip>
      </div>
    </div>

    <LoadingSpinner v-if="analyticsStore.loading" />

    <div class="overview">
      <div class="overview--left">
        <div class="overview--selection" data-step="1">
          <FloatLabel>
            <Select
              id="chosen_event"
              v-model="analyticsStore.chosenEvent"
              filter
              :autoFilterFocus="true"
              :disabled="analyticsStore.loading"
              :options="analyticsStore.reportableEvents"
              :virtualScrollerOptions="{ itemSize: 36 }"
              optionLabel="name"
            />
            <label for="chosen_event">Select Event</label>
          </FloatLabel>
        </div>
        <div v-if="analyticsStore.chosenEventDetails" class="overview--details">
          <div><IconLocation /> {{ eventLocation }}</div>
          <div>
            <IconDate /> {{ chosenEventStart }} &nbsp; - &nbsp; <IconDate /> {{ chosenEventEnd }}
          </div>
        </div>
      </div>
      <div class="overview--right" data-step="2">
        <div class="overview--right-inner">
          <div class="overview--configure">
            <span><IconCalendarTop /></span>
            Event Configuration
          </div>
          <div class="overview--stats">
            <div class="overview--stat">
              <span>Zones</span>
              {{ analyticsStore.chosenEventDetails?.zones?.length || '-' }}
            </div>

            <div class="overview--stat">
              <span>Clusters</span>
              {{ analyticsStore.chosenEventDetails?.clusters?.length || '-' }}
            </div>
          </div>
        </div>
        <div class="overview--button">
          <Button
            severity="secondary"
            outlined
            @click="openDetailsModal"
            :disabled="!analyticsStore.chosenEventDetails"
          >
            View Details
          </Button>

          <EventDetails
            v-if="showDetailsModal"
            :eventId="analyticsStore.chosenEventDetails?.id"
            @modalClosed="detailsModalClosed"
          />
        </div>
      </div>
    </div>

    <template v-if="!analyticsStore.loading && analyticsStore.chosenEvent">
      <div v-if="isFutureEvent" class="future-event">
        <img src="/widget-preview.png" />

        <div
          class="p-dialog p-component p-confirmdialog"
          role="alertdialog"
          aria-labelledby="pv_id_8_header"
          aria-modal="true"
          data-pc-name="dialog"
          pc18=""
          data-pc-section="root"
          data-pd-focustrap="true"
        >
          <span
            class="p-hidden-accessible p-hidden-focusable"
            tabindex="0"
            role="presentation"
            aria-hidden="true"
            data-p-hidden-accessible="true"
            data-p-hidden-focusable="true"
            data-pc-section="firstfocusableelement"
          />
          <div class="p-dialog-confirm">
            <IconTimer />
            <div class="modal-header">This event is not live yet.</div>
            <p>To view and update the event configuration, click the button below.</p>

            <div class="p-dialog-confirm--footer">
              <Button @click="editEvent">Edit Event</Button>
            </div>
          </div>
          <span
            class="p-hidden-accessible p-hidden-focusable"
            tabindex="0"
            role="presentation"
            aria-hidden="true"
            data-p-hidden-accessible="true"
            data-p-hidden-focusable="true"
            data-pc-section="lastfocusableelement"
          />
        </div>

        <EventEdit
          v-if="editEventId"
          v-model:eventId="editEventId"
          @onEventSave="onEventSave"
          @onEventClose="onEventClose"
        />
      </div>
      <AnalyticsDashboard v-else />
    </template>
  </MainLayout>
</template>

<script setup lang="ts">
import { computed, ref } from 'vue';

import mixpanel from '@/mixpanel.ts';

import { Button, FloatLabel, Select } from 'primevue';

import MainLayout from '@/components/global/MainLayout.vue';
import LoadingSpinner from '@/components/global/LoadingSpinner.vue';
import PageHeader from '@/components/global/PageHeader.vue';
import Tooltip from '@/components/global/Tooltip.vue';

import AnalyticsDashboard from '@/components/analytics/AnalyticsDashboard.vue';
import EventDetails from '@/components/dialogs/EventDetails.vue';
import EventEdit from '@/components/dialogs/EventEdit.vue';
import FeatureFlag from '@/components/global/FeatureFlag.vue';

import IconCompare from '@/icons/analytics/IconCompare.svg?component';
import IconBenchmark from '@/icons/analytics/IconBenchmark.svg?component';
import IconDownload from '@/icons/analytics/IconDownload.svg?component';
import IconCalendarTop from '@/icons/analytics/IconCalendarTop.svg?component';
import IconDate from '@/icons/analytics/IconDate.svg?component';
import IconLocation from '@/icons/analytics/IconLocation.svg?component';
import IconTimer from '@/icons/IconTimer.svg?component';
import IconPremium from '@/icons/IconPremium.svg?component';

import { getEventReport } from '@/utils/api';

import { useEventsStore } from '@/stores/events';
const eventsStore = useEventsStore();

import { useAnalyticsStore } from '@/stores/analytics';
const analyticsStore = useAnalyticsStore();

import { useUserStore } from '@/stores/user';
const userStore = useUserStore();

import { useToastStore } from '@/utils/toast';
const toast = useToastStore();

import { useRouter } from 'vue-router';
const router = useRouter();

const showDetailsModal = ref(false);
const editEventId = ref<number | null>(null);

eventsStore.getEventListData();

const eventLocation = computed(() => {
  if (analyticsStore.chosenEventDetails) {
    const locMatch = eventsStore.eventList?.location.find(
      (loc) => loc.id === analyticsStore.chosenEventDetails?.timezone_id,
    );
    return locMatch?.name || '';
  }
  return '';
});

const chosenEventStart = computed(() => {
  if (analyticsStore.chosenEventDetails) {
    return analyticsStore.chosenEventDetails.event_start_str;
  }
  return '';
});

const chosenEventEnd = computed(() => {
  if (analyticsStore.chosenEventDetails) {
    return analyticsStore.chosenEventDetails.event_end_str;
  }
  return '';
});

const isFutureEvent = computed(() => {
  if (analyticsStore.chosenEventDetails) {
    return !analyticsStore.chosenEventDetails.event_started;
  }
  return false;
});

const downloadReport = () => {
  if (analyticsStore.chosenEvent?.id) {
    getEventReport(userStore.accessToken, { event_id: analyticsStore.chosenEvent.id }).then(
      (resp) => {
        const fileURL = window.URL.createObjectURL(new Blob([resp.data]));
        const fileLink = document.createElement('a');
        fileLink.href = fileURL;
        fileLink.setAttribute('download', `${analyticsStore.chosenEvent?.name}.xlsx`);
        document.body.appendChild(fileLink);
        fileLink.click();
      },
      () => {
        const reportError = {
          response: {
            data: {
              message: 'Report could not be downloaded. Please contact Meshh Support.',
            },
          },
        };
        toast.error(reportError);
      },
    );
    mixpanel.track(`Download Full Event Report`, {
      'Event Name': analyticsStore.chosenEvent.name,
      'Event UUID': analyticsStore.chosenEvent.uuid,
      'Event ID': analyticsStore.chosenEvent.id,
    });
  }
};

const openDetailsModal = () => {
  showDetailsModal.value = true;
};

const detailsModalClosed = () => {
  showDetailsModal.value = false;
};

const editEvent = () => {
  if (analyticsStore.chosenEvent) {
    editEventId.value = analyticsStore.chosenEvent.id;
  }
};

const onEventSave = () => {
  editEventId.value = null;
  router.go(0);
};

const onEventClose = () => {
  editEventId.value = null;
};
</script>

<style lang="scss">
.overview {
  @include mixins.flex-center($horiz: space-between, $gap: variables.$vantage-base-sizing-xs);
  align-items: stretch;
  background: variables.$vantage-base-primitives-color-background-400;
  border: 1px solid variables.$vantage-base-primitives-color-border-200;
  border-radius: variables.$vantage-base-sizing-xxs;
  margin-bottom: variables.$vantage-base-sizing-lg;
}

.overview--left {
  @include sizing.padding();
  border-right: 1px solid variables.$vantage-base-primitives-color-border-200;
  flex: 1;
  flex-basis: calc(60% - 1px);

  .overview--selection {
    @include sizing.padding('lg', 0, 'xs', 0);
  }

  .p-select.p-component {
    background: variables.$vantage-base-primitives-color-background-100;
  }

  .overview--details {
    @include mixins.flex-center($wrap: true);
    @include typography.font-body($size: sm, $weight: normal);
    justify-content: space-between;

    > div {
      @include mixins.flex-center($horiz: flex-start, $gap: variables.$vantage-base-sizing-xxxs);
    }
  }
}

.overview--right {
  @include sizing.padding();
  display: flex;
  flex: 0;
  flex-basis: 450px;

  .overview--right-inner {
    @include mixins.flex-center($horiz: flex-start, $wrap: true);
    flex: 0;
    flex-basis: 225px;
  }

  .overview--stats {
    @include mixins.flex-center($horiz: flex-start, $gap: variables.$vantage-base-sizing-sm);
    flex: 0;
    flex-basis: 100%;
    margin-top: auto;
  }

  .overview--stat:first-of-type {
    border-right: 1px solid variables.$vantage-base-primitives-color-border-200;
    padding-right: variables.$vantage-base-sizing-sm;
  }

  .overview--stat {
    @include typography.font-display($size: xs, $weight: bold);
    color: variables.$vantage-base-primitives-color-tertiary-500;

    span {
      @include typography.font-body($size: sm, $weight: medium);
      color: variables.$vantage-base-primitives-color-tertiary-400;
      display: block;
      text-transform: uppercase;
    }
  }
}

.overview--button {
  @include mixins.flex-center($horiz: flex-end);
  flex: 1;
  margin-left: auto;

  .p-button-outlined.p-button-secondary {
    @include sizing.padding('xxxs', 'xs');
    border: 1px solid variables.$vantage-base-primitives-color-tertiary-400;
    border-radius: variables.$vantage-base-sizing-xxs;
    color: variables.$vantage-base-primitives-color-tertiary-500;

    &:not(:disabled):hover {
      background: variables.$vantage-base-primitives-color-background-100;
      border-color: variables.$vantage-base-primitives-color-tertiary-400;
    }
  }
}

.overview--configure {
  @include mixins.flex-center($horiz: flex-start, $gap: variables.$vantage-base-sizing-xxs);

  span {
    @include mixins.flex-center();
    background: variables.$vantage-base-primitives-color-background-100;
    border-radius: 50%;
    height: 40px;
    width: 40px;
  }
}

.widget .apexcharts-tooltip.apexcharts-theme-light {
  background: variables.$vantage-base-primitives-color-tertiary-500;
  border: 1px solid variables.$vantage-base-primitives-color-border-200;
  color: variables.$vantage-base-primitives-color-background-100;

  .apexcharts-tooltip-title {
    background-color: transparent;
    border: none;
    margin-bottom: 0;
  }
}

.overview-overline {
  @include mixins.flex-center($horiz: space-between);
  margin-bottom: variables.$vantage-base-sizing-sm;

  .overline--headline {
    @include typography.font-display($size: lg, $weight: bold);
    color: variables.$vantage-base-primitives-color-tertiary-500;
  }

  .overline--controls {
    @include mixins.flex-center($gap: variables.$vantage-base-sizing-xs);
    margin-left: auto;

    .p-button.p-button-outlined {
      @include sizing.padding('xxxs', 'xxs');
    }

    .p-button {
      @include mixins.flex-center($gap: variables.$vantage-base-sizing-xxs);

      .premium-icon {
        fill: variables.$vantage-base-primitives-color-secondary-500;
        width: 16px;
        height: 16px;
      }
    }
  }
}

.overview-submenu {
  @include mixins.flex-center();
  margin-bottom: variables.$vantage-base-sizing-lg;

  .overview-submenu__left {
    @include mixins.flex-center($gap: variables.$vantage-base-sizing-xxxs);
    margin-right: auto;

    .p-select.p-component {
      min-width: 200px;
    }
  }

  .overview-submenu__right {
    @include mixins.flex-center($gap: variables.$vantage-base-sizing-xxxs);
    margin-left: auto;
  }

  .overview-submenu__title {
    @include typography.font-display($size: xs, $weight: medium);
    color: variables.$vantage-base-primitives-color-tertiary-500;
    margin-right: variables.$vantage-base-sizing-md;
  }

  .p-button:not(.p-button-primary):disabled {
    background: transparent;
    border: 1px solid variables.$vantage-base-primitives-color-border-200;
  }

  .p-button:not(.p-button-outlined):disabled {
    background: variables.$vantage-base-primitives-color-secondary-400;
    border: 1px solid variables.$vantage-base-primitives-color-border-200;
  }
}

.future-event {
  position: relative;

  img {
    width: 100%;
  }

  .p-dialog {
    display: flex;
    flex-direction: column;
    left: calc(50% - 225px);
    pointer-events: auto;
    position: absolute;
    top: calc(50% - 150px);
  }

  .p-dialog .p-dialog-confirm {
    svg {
      @include sizing.margin('xxl', 'sm', 'xxs', 'sm');
    }

    p {
      color: variables.$vantage-base-primitives-color-tertiary-400;
    }

    .modal-header {
      @include sizing.padding('xxs', 'sm');
      justify-content: center;
    }
  }
}
</style>
